import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
} from '@mui/material';
import FormActionButtons from 'components/ui/form/FormActionButtons';
import FormTextField from 'components/ui/form/FormTextField';
import FormSwitchField from 'components/ui/form/FormSwitchField';
import { useFormik } from 'formik';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useFormSubmit from 'hooks/useFormSubmit';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import * as yup from 'yup';

const validationSchema = yup.object({
  name: yup
    .string()
    .min(3, { label: 'settings.staff.validation.nameMin', value: 4 })
    .required('settings.staff.validation.name'),
});

const AddEditModal = (props) => {
  const { open, setOpen, setRefetch, itemId, setItemId } = props;
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const [hasError, setHasError] = useState(false);
  const { submit, isSubmitting } = useFormSubmit();

  const formik = useFormik({
    initialValues: {
      name: '',
      iscountable: '0',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (itemId) {
        submit(
          { type: 'postUpdate', contentType: 'simple' },
          values,
          '/admin/expense-category',
          values.name,
          itemId,
          false,
          handleFinishRequest
        );
      } else
        submit(
          { type: 'post', contentType: 'simple' },
          values,
          '/admin/expense-category',
          values.name,
          null,
          false,
          handleFinishRequest
        );
    },
  });

  const { isLoading, isFetching } = useQuery({
    queryKey: '/admin/expense-category/edit/{itemId}',
    queryFn: async function () {
      const response = await axiosPrivate.get(
        `/admin/expense-category/edit/${itemId}`
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      formik.setValues({
        name: data.name,
        iscountable: data.iscountable,
      });
    },
    enabled: !hasError && !!itemId,
    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  const handleFinishRequest = () => {
    setRefetch(true);
    handleClose();
  };

  const handleClose = () => {
    setItemId('');
    setOpen(false);
    formik.resetForm();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      maxWidth='lg'
      PaperProps={{
        className: '!base-border !shadow-none',
      }}
      disableEscapeKeyDown={true}
    >
      <DialogTitle
        id='alert-dialog-title'
        className='bg-gray-100 text-center !mb-2'
      >
        {itemId ? (
          <span>{t('productCategory.editTitle')}</span>
        ) : (
          <span>{t('productCategory.addTitle')}</span>
        )}
      </DialogTitle>

      <DialogContent>
        {isLoading || isFetching ? (
          <div className='circular-progress-box py-5'>
            <CircularProgress size={35} />
          </div>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid
              container
              spacing={{ xs: 2, sm: 3, lg: 3 }}
              rowSpacing={1}
              columns={{ xs: 12, sm: 12, lg: 12 }}
            >
              <Grid item={true} lg={9} sm={9} xs={9}>
                <FormTextField
                  delay={0.1}
                  label={t('common.fields.simpleName')}
                  fieldName='name'
                  formik={formik}
                />
              </Grid>
              <Grid item={true} sm={3} xs={3}>
                <FormSwitchField
                  delay={0.5}
                  label={'Sanaladi'}
                  fieldName='iscountable'
                  formik={formik}
                  options={[
                    {
                      value: '1',
                      checked: true,
                    },
                    {
                      value: '0',
                      checked: false,
                    },
                  ]}
                />
              </Grid>

              <Grid item={true} sm={12} xs={12}>
                <FormActionButtons
                  delay={0.6}
                  isSubmitting={isSubmitting}
                  formType='dialog'
                  setOpen={setOpen}
                  reset={formik.resetForm}
                />
              </Grid>
            </Grid>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default memo(AddEditModal);
